import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import { useMapLayersState } from 'state/useMapLayersState';
import { useLayersDrawerState } from 'features/mapping/hooks/useLayersDrawerState';
import { MapLayers } from 'features/mapping/mapping.constants';
import { useAQIStationsLayerEnabled } from 'features/mapping/hooks/useAQIStationsLayerEnabled';
import { useLightningLayerEnabled } from 'features/mapping/hooks/useLightningLayerEnabled';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { AirLegend } from './AirLegend';
import { FireLegend } from './FireLegend';
import { MapCoverageLegend } from './MapCoverageLegend';
import { SatelliteLegend } from './SatelliteLegend';
import { AircraftLegend } from './AircraftLegend';
import { RadioTowersLegend } from './RadioTowersLegend';
import { EvacZonesLegend } from './EvacZonesLegend';
import { LocationsLegend } from './LocationsLegend';
import { CamerasLegend } from './CamerasLegend';
import { WaterLevelLegends } from './WaterLevelLegends';
import { AQILegend } from './AQILegend';
import { HazardsLegend } from './HazardsLegend';
import { FemaFloodHazardLegend } from './FemaFloodHazardLegend';
import { LightningLegend } from './LightningLegend';
import { FloodsLegend } from './FloodsLegend';

const useStyles = makeStyles()((theme) => ({
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 1,
  },
  contentContainer: {
    paddingTop: 16,
    paddingBottom: 16,
    '& .MuiFormControl-root': {
      width: '100%',
    },
    [theme.breakpoints.down(480)]: {
      paddingTop: 0,
    },
  },
  mobileHandle: {
    height: 6,
    width: 80,
    borderRadius: Number(theme.shape.borderRadius) * 2,
    backgroundColor: theme.palette.grey[300],
    marginTop: 8,
  },
}));

export const LegendDrawerContent = forwardRef<HTMLDivElement>(
  (_, ref): JSX.Element => {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('phone'));
    const { mapLayers } = useMapLayersState();
    const { closeLayersDrawer } = useLayersDrawerState();
    const aqiStationsLayerEnabled = useAQIStationsLayerEnabled();
    const { showLightningLayer } = useLightningLayerEnabled();
    const isNgfsEnabled = useReleaseFlag('show_ngfs_detections_in_frontend');

    return (
      <div style={{ position: 'relative' }} ref={ref}>
        {!isPhone && (
          <IconButton
            aria-label={t('common.closeLegendDrawer')}
            className={classes.closeButton}
            onClick={closeLayersDrawer}
            color="inherit"
            size="large"
          >
            <CloseIcon />
          </IconButton>
        )}

        <Box className={classes.contentContainer}>
          <FireLegend />

          <FloodsLegend />

          <LocationsLegend />

          <HazardsLegend />

          <EvacZonesLegend />

          {mapLayers.includes(MapLayers.FLIGHT_TRACKER) && <AircraftLegend />}

          {(mapLayers.includes(MapLayers.VIIRS) ||
            mapLayers.includes(MapLayers.MODIS) ||
            (isNgfsEnabled &&
              mapLayers.includes(MapLayers.NGFS_DETECTIONS))) && (
            <SatelliteLegend />
          )}

          {mapLayers.includes(MapLayers.PURPLE_AIR) &&
            (aqiStationsLayerEnabled ? <AQILegend /> : <AirLegend />)}

          {mapLayers.includes(MapLayers.LIGHTNING) && showLightningLayer && (
            <LightningLegend />
          )}

          {mapLayers.includes(MapLayers.RADIO_TOWERS) && <RadioTowersLegend />}

          {mapLayers.includes(MapLayers.CAMERAS) && <CamerasLegend />}

          {mapLayers.includes(MapLayers.WATER_LEVEL) && <WaterLevelLegends />}

          {mapLayers.includes(MapLayers.FEMA_FLOOD_HAZARD) && (
            <FemaFloodHazardLegend />
          )}
        </Box>

        <Box className={classes.contentContainer}>
          <MapCoverageLegend />
        </Box>
      </div>
    );
  },
);

LegendDrawerContent.displayName = 'LegendDrawerContent';
